import type { ModuleType, Replacement } from '../app/react/components/EditorWithReplacements/types';
import type MiniUser from './MiniUser';

export type RecipientType =
    | 'EMPLOYEE'
    | 'MANAGER'
    | 'MANAGERINDIRECT'
    | 'HR'
    | 'REVIEWEE'
    | 'REVIEWER'
    | 'REVIEWERSECOND';

export type NotificationType = 'REQUEST' | 'NUDGE' | 'NOTIFY';

export type TriggerType = 'AUTOMATIC' | 'MANUAL';

export type Grouping = 'triggerType' | 'recipientType';

type EditPermission = 'ALLOWED' | 'RESTRICTED' | 'FORBIDDEN';

export interface EmailTemplateMiniDto {
    cycleId: string | null;
    enabled: boolean;
    templateType: EmailTemplateTypeMiniDto;
}

interface EmailTemplateTypeMiniDto {
    type: EmailTemplateType;
    name: string;
}

export interface EmailTemplateDto extends EmailTemplateMiniDto {
    title: string;
    body: string;
    body2: string;
    modified: boolean;
    modifiedAt: number | null;
    modifiedBy: MiniUser | null;
    templateType: EmailTemplateTypeDto;
    replacementsForPreview?: {
        [key: string]: string;
    };
}

export interface EmailTemplateTypeDto extends EmailTemplateTypeMiniDto {
    moduleType: ModuleType;
    recipientType: RecipientType;
    notificationType: NotificationType;
    triggerType: TriggerType;
    canBeDisabled: boolean;
    editPermission: EditPermission;
    dualBodied: boolean;
    description: string;
    shortName: string;
    contentDescription: string;
    replacements: Replacement[];
}

export interface EmailTemplatePreview {
    id: string;
    title: string;
    body: string;
    body2: string;
    contentDescription: string;
    signature: string;
}

export type EmailTemplateType =
    | 'REVIEW_NUDGE_MANAGER'
    | 'REVIEW_INFORM_SECONDARY_REVIEWER_NUDGE_MANAGER'
    | 'REVIEW_NUDGE_MANAGER_UNSPECIFIC'
    | 'REVIEW_INFORM_MANAGER_UNSPECIFIC'
    | 'REVIEW_INFORM_SECONDARY_REVIEWER_UNSPECIFIC'
    | 'REVIEW_NUDGE_SECONDARY_REVIEWER_UNSPECIFIC'
    | 'REVIEW_INFORM_CUSTOM_REVIEWER_UNSPECIFIC'
    | 'REVIEW_NUDGE_REPORT'
    | 'REVIEW_HID_BY_EMPLOYEE'
    | 'REVIEW_HID_BY_MANAGER'
    | 'REVIEW_INFORM_TEAM_MEMBER'
    | 'REVIEW_SHARE_WITH_MANAGER'
    | 'REVIEW_SHARE_WITH_EMPLOYEE'
    | 'REVIEW_SIGNED_BY_REPORT'
    | 'REVIEW_SIGNED_BY_MANAGER'
    | 'REVIEW_CHANGED_BY_REVIEWEE'
    | 'REVIEW_CHANGED_BY_MANAGER'
    | 'REVIEW_INDICATED_BY_MANAGER'
    | 'FEEDBACK_REQUEST_NUDGE_REVIEWEE'
    | 'FEEDBACK_REQUEST_INFORM_MANAGER_TO_NOMINATE'
    | 'FEEDBACK_REQUEST_NUDGE_MANAGER'
    | 'FEEDBACK_REQUEST_OUTSTANDING_REVIEWS_NUDGE'
    | 'NUDGE_EMPLOYEE_TO_PROVIDE_SINGLE_FEEDBACK'
    | 'FEEDBACK_REQUEST_OUTSTANDING_REVIEWS_NOTIFICATION'
    | 'FEEDBACK_REQUEST_APPROVEABLE_REVIEWS_NOTIFICATION'
    | 'FEEDBACK_REQUEST_APPROVEABLE_REVIEWS_NUDGE'
    | 'FEEDBACK_REQUEST_INSTRUCTION_REVIEWEE'
    | 'FEEDBACK_REQUEST_NOTIFICATION_ABOUT_RELEASED_FEED_BACK'
    | 'REQUEST_FEEDBACK_NOTIFICATION_ABOUT_RELEASED_FEED_BACK'
    | 'FEEDBACK_REQUEST_NOTIFICATION_ABOUT_RELEASING_FEED_BACK_SOON'
    | 'FEEDBACK_REQUEST_NOTIFICATION_ABOUT_RELEASED_FEEDBACK_MANAGER'
    | 'FEEDBACK_REQUEST_PARTICIPANT_DECLINED'
    | 'FEEDBACK_REQUEST_PARTICIPANT_DECLINED__REVIEWEE'
    | 'NOTIFY_ADMIN_TO_REVIEW_FEEDBACK_CYCLE_AFTER_REVIEWER_SELECTION_DEADLINE_PASSED'
    | 'FEEDBACK_RELEASED_DAILY_NOTIFICATION'
    | 'FEEDBACK_RELEASED_DAILY_NOTIFICATION_TO_MANAGERS'
    | 'OBJECTIVE_UPDATE_FOR_MANAGER'
    | 'OBJECTIVE_UPDATE_FOR_USER'
    | 'OBJECTIVES_DUE'
    | 'OBJECTIVES_NUDGE_INITIAL'
    | 'OBJECTIVES_NUDGE_MANAGER'
    | 'OBJECTIVES_NUDGE_EMPLOYEE'
    | 'OBJECTIVES_NUDGE_EMPLOYEE_ABOUT_OBJECTIVE'
    | 'PASSWORD_CHANGE_INSTRUCTIONS'
    | 'PASSWORD_WELCOME_INSTRUCTIONS'
    | 'PASSWORD_RESET'
    | 'USER_INVITED'
    | 'GUEST_INVITED'
    | 'USER_PROFILE_NUDGE'
    | 'USER_DIGEST'
    | 'INFORM_MANAGER_ABOUT_NEW_REPORTS'
    | 'MESSAGE_NOTIFICATION'
    | 'MESSAGE_CHANGED_BY_NON_AUTHOR'
    | 'PRAISE_NOTIFICATION'
    | 'PRAISE_CHANGED_BY_NON_AUTHOR'
    | 'MEETING_NUDGE'
    | 'FEEDBACK_REQUEST_NOTIFY_MANAGER_ABOUT_NOMINATIONS'
    | 'OBJECTIVE_MENTION_NOTIFICATION'
    | 'SURVEY_NOTIFICATION';

export const getLaddaId = (template: EmailTemplateMiniDto) => template.templateType.type + (template.cycleId ?? '');
